/**
 * Task P: store-app UX round — bottom tab bar (short labels), Home dashboard, first-run setup, POS gates + split layout.
 * One row per key: [key, English, 中文]. The app ships English + 中文 only; Malay falls back to English.
 */
const ROWS = [
  // bottom tab bar: short labels (never truncated)
  ["tp.tab.home", "Home", "首页"],
  ["tp.tab.pos", "POS", "收银"],
  ["tp.tab.bookings", "Bookings", "预约"],
  ["tp.tab.clients", "Clients", "顾客"],
  ["tp.tab.clock", "Clock", "打卡"],
  ["tp.tab.photos", "Photos", "作品照"],
  ["tp.tab.cash", "Cash", "钱箱"],
  ["tp.tab.device", "This device", "本设备"],
  ["tp.tab.more", "More", "更多"],
  ["tp.more.title", "More", "更多"],
  ["tp.more.attendance", "Attendance", "打卡"],
  ["tp.more.photos", "Work photos", "作品照"],
  ["tp.more.cash", "Cash drawer", "钱箱"],
  // Home dashboard
  ["tp.home.title", "Home", "首页"],
  ["tp.home.hello", "Hi, {name}", "你好，{name}"],
  ["tp.home.myToday", "My day", "我的今天"],
  ["tp.home.sales", "Sales today", "今日业绩"],
  ["tp.home.customers", "Customers", "顾客数"],
  ["tp.home.services", "Services done", "完成服务"],
  ["tp.home.commission", "My commission today", "今日提成"],
  ["tp.home.week", "This week", "本周"],
  ["tp.home.month", "This month", "本月"],
  ["tp.home.salesN", "{n} sales", "{n} 单"],
  ["tp.home.branchToday", "Branch today", "门店今日"],
  ["tp.home.branchSales", "Branch sales", "门店营业额"],
  ["tp.home.branchOrders", "Paid sales", "已结账单数"],
  ["tp.home.branchCustomers", "Customers served", "接待顾客"],
  ["tp.home.business", "Business", "营业状态"],
  ["tp.home.open", "Open for business", "营业中"],
  ["tp.home.closed", "Not open yet", "未开始营业"],
  ["tp.home.openedAt", "Since {time}", "开始于 {time}"],
  ["tp.home.cash", "Cash in drawer", "钱箱现金"],
  ["tp.home.cashClosed", "Drawer closed", "钱箱未开"],
  ["tp.home.appts", "Appointments today", "今日预约"],
  ["tp.home.next", "Next: {time} · {name}", "下一位：{time} · {name}"],
  ["tp.home.noNext", "No more appointments today", "今天没有更多预约"],
  ["tp.home.newSale", "New sale", "开始新单"],
  ["tp.home.clock", "Clock in / out", "上下班打卡"],
  ["tp.home.appointments", "Appointments", "预约"],
  ["tp.home.estimate", "Commission is an estimate until payroll is approved.", "工资审批前，提成均为预估。"],
  // POS
  ["tp.pos.newSale", "New sale", "开始新单"],
  ["tp.pos.newSaleHint", "Choose the customer first, then add services and products.", "先选择顾客，再添加服务和产品。"],
  ["tp.pos.services", "Services", "服务"],
  ["tp.pos.products", "Products", "产品"],
  ["tp.pos.packages", "Packages", "套餐"],
  ["tp.pos.topup", "Top-up", "储值"],
  ["tp.pos.search", "Search services and products", "搜索服务和产品"],
  ["tp.pos.allCats", "All", "全部"],
  ["tp.pos.noMatch", "Nothing matches “{q}”.", "没有与“{q}”匹配的项目。"],
  ["tp.pos.topupNeedCustomer", "Choose a member first to top up their credit.", "请先选择会员再储值。"],
  ["tp.pos.topupOff", "Credit top-ups are off for this salon.", "本店未开启储值。"],
  ["tp.pos.cartBar", "{n} items · {amount}", "{n} 项 · {amount}"],
  ["tp.pos.view", "View", "查看"],
  ["tp.pos.cart", "Current sale", "当前订单"],
  ["tp.pos.packagesAvail", "{n} package(s) available", "可用套餐 {n} 个"],
  // first-run setup
  ["tp.setup.title", "Set up this app", "设置本应用"],
  ["tp.setup.skip", "Skip setup", "跳过设置"],
  ["tp.setup.next", "Next", "下一步"],
  ["tp.setup.back", "Back", "上一步"],
  ["tp.setup.finish", "Finish", "完成"],
  ["tp.setup.step", "Step {n} of {total}", "第 {n} 步，共 {total} 步"],
  ["tp.setup.welcome.title", "Welcome, {name}", "欢迎，{name}"],
  ["tp.setup.welcome.body", "A few quick questions to get this device ready. It takes about a minute, and you can change everything later under This device.", "回答几个简单问题即可让本设备准备就绪，大约一分钟。之后都可以在“本设备”中修改。"],
  ["tp.setup.salon.title", "Your salon and branch", "确认门店与分店"],
  ["tp.setup.salon.body", "Sales, appointments and attendance on this device go to this branch.", "本设备上的销售、预约和打卡都记到此分店。"],
  ["tp.setup.salon.salon", "Salon", "门店"],
  ["tp.setup.salon.branch", "Branch", "分店"],
  ["tp.setup.lang.title", "Language", "语言"],
  ["tp.setup.lang.body", "Choose the language for this app.", "选择本应用的显示语言。"],
  ["tp.setup.device.title", "How is this device used?", "这台设备怎么用？"],
  ["tp.setup.device.shared", "Shared front-desk tablet", "前台共用平板"],
  ["tp.setup.device.sharedHelp", "Several people use it at the counter. Staff unlock it with their own PIN (clocking in is done on each person's own phone).", "多人在前台使用。员工用自己的 PIN 解锁（上下班打卡在各自的手机上完成）。"],
  ["tp.setup.device.personal", "My personal device", "我的个人设备"],
  ["tp.setup.device.personalHelp", "Only you use it. Protect it with the app lock.", "只有你使用。建议开启应用锁。"],
  ["tp.setup.lock.title", "App lock", "应用锁"],
  ["tp.setup.lock.body", "Ask for fingerprint, face or the device PIN when the app comes back after a while.", "应用在后台一段时间后返回时，需要指纹、面容或设备密码解锁。"],
  ["tp.setup.lock.on", "Turn on app lock", "开启应用锁"],
  ["tp.setup.lock.bio", "Fingerprint / face unlock is available on this device.", "本设备支持指纹/面容解锁。"],
  ["tp.setup.lock.noSecure", "This device has no screen lock, so the app lock can't be used. Set one in the device settings.", "本设备未设置锁屏，无法使用应用锁。请先在系统设置中设置锁屏。"],
  ["tp.setup.pin.title", "Your staff PIN", "你的员工 PIN"],
  ["tp.setup.pin.body", "4–6 digits. You use it to switch to yourself on the shared counter tablet. Don't share it. (Clock in and out on your own phone.)", "4–6 位数字，用于在共用收银平板上切换成你自己。请勿告诉他人。（上下班请用自己的手机打卡。）"],
  ["tp.setup.pin.has", "You already have a PIN. Enter a new one to change it, or continue.", "你已设置 PIN。输入新的 PIN 可修改，或直接继续。"],
  ["tp.setup.pin.new", "New PIN", "新 PIN"],
  ["tp.setup.pin.confirm", "Repeat PIN", "再次输入 PIN"],
  ["tp.setup.pin.mismatch", "The two PINs don't match.", "两次输入的 PIN 不一致。"],
  ["tp.setup.pin.save", "Save PIN", "保存 PIN"],
  ["tp.setup.pin.saved", "PIN saved", "PIN 已保存"],
  ["tp.setup.tour.title", "Quick tour", "快速导览"],
  ["tp.setup.tour.1.title", "Home", "首页"],
  ["tp.setup.tour.1.body", "Your sales, customers and commission today, plus shortcuts to a new sale, clocking in and appointments.", "查看今天的业绩、顾客和提成，并快速开始新单、打卡和查看预约。"],
  ["tp.setup.tour.2.title", "POS and customers", "收银与顾客"],
  ["tp.setup.tour.2.body", "Start a sale for a customer or member, add services and products, use packages, then charge.", "为顾客或会员开单，添加服务和产品，使用套餐，然后收款。"],
  ["tp.setup.tour.3.title", "Bookings, attendance and photos", "预约、打卡与作品照"],
  ["tp.setup.tour.3.body", "See today's appointments, clock in and out, and take before / after photos of your work.", "查看今日预约、上下班打卡，并拍摄作品前后对比照。"],
  ["tp.setup.tour.4.title", "Cash drawer and this device", "钱箱与本设备"],
  ["tp.setup.tour.4.body", "Open and close business with the cash drawer. App lock, notifications and this setup are under More → This device.", "通过钱箱开始和结束营业。应用锁、通知和本设置在“更多 → 本设备”中。"],
  ["tp.setup.biz.title", "Open for business?", "现在开始营业？"],
  ["tp.setup.biz.body", "Count the cash in the drawer and open business now, or do it later from Home or POS.", "清点钱箱现金并立即开始营业，或稍后在首页或收银页操作。"],
  ["tp.setup.biz.now", "Open business now", "现在开始营业"],
  ["tp.setup.biz.later", "Later", "稍后"],
  ["tp.setup.biz.isOpen", "Business is already open at this branch.", "本分店已在营业中。"],
  ["tp.setup.done", "All set", "设置完成"],
  ["tp.setup.reopen", "Run setup again", "重新运行设置"],
  ["tp.setup.reopenHelp", "Language, device use, app lock, your PIN and the quick tour.", "语言、设备用途、应用锁、你的 PIN 和快速导览。"],
  ["tp.device.role", "Device use", "设备用途"],
  ["apiError.pin_taken", "Another team member already uses this PIN. Choose a different one.", "该 PIN 已被其他同事使用，请换一个。"],
] as const;

export type TpKey = (typeof ROWS)[number][0];
const col = (i: 1 | 2) => Object.fromEntries(ROWS.map((r) => [r[0], r[i]])) as Record<TpKey, string>;

export const taskP = { en: col(1), zh: col(2), ms: col(1) };
